import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { pageTitle } from "ember-page-title";
import ChannelRow from "#components/channel-row.gts";
import EventLog, { EventLogger } from "#components/sandbox/event-log.gts";
import { Channel } from "#models/channel.ts";
import { Step } from "#models/step.ts";
import { Sound } from "#models/sound.ts";
import { fn } from "@ember/helper";

export default class SandboxChannelRowTemplateComponent extends Component {
  @tracked currentTick = 0;
  @tracked isPlaying = false;
  private timer: number | null = null;
  logger = new EventLogger();

  // Single channel for isolated demo
  @tracked singleChannel = new Channel({
    sound: Sound.findById("kick"),
    volume: 1,
    isMuted: false,
    steps: Array.from(
      { length: 16 },
      (_, i) => new Step({ velocity: i % 4 === 0 ? 1.0 : i % 2 === 0 ? 0.5 : 0 }),
    ),
  });

  // Multi-channel track stack
  @tracked kickChannel = new Channel({
    sound: Sound.findById("kick"),
    steps: Array.from({ length: 16 }, (_, i) => new Step({ velocity: i % 4 === 0 ? 1.0 : 0 })),
  });

  @tracked snareChannel = new Channel({
    sound: Sound.findById("snare"),
    steps: Array.from(
      { length: 16 },
      (_, i) => new Step({ velocity: (i - 4) % 8 === 0 ? 1.0 : 0 }),
    ),
  });

  @tracked hihatChannel = new Channel({
    sound: Sound.findById("hihat_closed"),
    steps: Array.from({ length: 48 }, (_, i) => new Step({ velocity: i % 2 === 0 ? 0.5 : 0 })),
  });

  handlePreviewSound = (sound: Sound) => {
    this.logger.log(`Audio Preview Triggered: ${sound.id}`);
  };

  handleDeleteChannel = (channel: Channel) => {
    this.logger.log(`Delete channel triggered with`, channel);
  };

  handleToggleMute = (channel: Channel) => {
    channel.isMuted = !channel.isMuted;
    this.logger.log(`Toggle mute triggered for ${channel.sound.name}: isMuted=${channel.isMuted}`);
  };

  handleNextSound = (channel: Channel) => {
    this.logger.log(`next sound triggered with`, channel);
    channel.sound = Sound.next(channel.sound);
  };

  handlePreviousSound = (channel: Channel) => {
    this.logger.log(`next sound triggered for`, channel);
    channel.sound = Sound.previous(channel.sound);
  };

  handleAddStep = (channel: Channel) => {
    this.logger.log(`add step triggered for`, channel);
    channel.addStep();
  };

  handleRemoveStep = (channel: Channel) => {
    this.logger.log(`remove step triggered for`, channel);
    channel.removeStep();
  };

  handleStepClick = (step: Step, channel: Channel, index: number) => {
    step.cycleVelocity();
    this.logger.log(`Step ${index + 1} clicked on ${channel}: velocity=${step.velocity}`);
  };

  togglePlay = () => {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.play();
    }
  };

  play = () => {
    this.isPlaying = true;
    this.timer = window.setInterval(() => {
      this.currentTick = this.currentTick + 1;
    }, 200);
  };

  stop = () => {
    this.isPlaying = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  reset = () => {
    this.currentTick = 0;
  };

  willDestroy(): void {
    super.willDestroy();
    this.stop();
  }

  <template>
    {{pageTitle "ChannelRow Sandbox"}}

    <section class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="px-4 py-2 text-xs font-bold rounded bg-amber-500 text-slate-950 cursor-pointer"
          {{on "click" this.togglePlay}}
        >
          <span>{{if this.isPlaying "Pause Sequencer" "Simulate Sequencer"}}</span>
        </button>
        <button
          type="button"
          class="px-4 py-2 text-xs font-bold rounded bg-slate-700 hover:bg-slate-600 text-white cursor-pointer"
          {{on "click" this.reset}}
        >
          Reset
        </button>
      </div>

      <div class="text-sm text-slate-400">
        Tick:
        <span
          class="font-bold font-mono text-amber-400 inline-block w-20 text-right"
        >{{this.currentTick}}</span>
      </div>
    </section>

    <section>
      <h2 class="mt-4 mb-2 font-bold text-xl">Single Channel</h2>

      <div class="">
        <ChannelRow
          @channel={{this.singleChannel}}
          @tick={{if this.isPlaying this.currentTick undefined}}
          @onPreviewSound={{this.handlePreviewSound}}
          @onDeleteChannel={{fn this.handleDeleteChannel this.singleChannel}}
          @onToggleMute={{fn this.handleToggleMute this.singleChannel}}
          @onNextSound={{fn this.handleNextSound this.singleChannel}}
          @onPreviousSound={{fn this.handlePreviousSound this.singleChannel}}
          @onAddStep={{fn this.handleAddStep this.singleChannel}}
          @onRemoveStep={{fn this.handleRemoveStep this.singleChannel}}
          @onStepClick={{this.handleStepClick}}
        />
      </div>
    </section>

    <section>
      <h2 class="mt-4 mb-2 font-bold text-xl">Multiple Channels</h2>

      <div class="space-y-3">
        <ChannelRow
          @channel={{this.kickChannel}}
          @tick={{if this.isPlaying this.currentTick undefined}}
          @onPreviewSound={{this.handlePreviewSound}}
          @onDeleteChannel={{fn this.handleDeleteChannel this.kickChannel}}
          @onToggleMute={{fn this.handleToggleMute this.kickChannel}}
          @onNextSound={{fn this.handleNextSound this.kickChannel}}
          @onPreviousSound={{fn this.handlePreviousSound this.kickChannel}}
          @onAddStep={{fn this.handleAddStep this.kickChannel}}
          @onRemoveStep={{fn this.handleRemoveStep this.kickChannel}}
          @onStepClick={{this.handleStepClick}}
        />
        <ChannelRow
          @channel={{this.snareChannel}}
          @tick={{if this.isPlaying this.currentTick undefined}}
          @onPreviewSound={{this.handlePreviewSound}}
          @onDeleteChannel={{fn this.handleDeleteChannel this.snareChannel}}
          @onToggleMute={{fn this.handleToggleMute this.snareChannel}}
          @onNextSound={{fn this.handleNextSound this.snareChannel}}
          @onPreviousSound={{fn this.handlePreviousSound this.snareChannel}}
          @onAddStep={{fn this.handleAddStep this.snareChannel}}
          @onRemoveStep={{fn this.handleRemoveStep this.snareChannel}}
          @onStepClick={{this.handleStepClick}}
        />
        <ChannelRow
          @channel={{this.hihatChannel}}
          @tick={{if this.isPlaying this.currentTick undefined}}
          @onPreviewSound={{this.handlePreviewSound}}
          @onDeleteChannel={{fn this.handleDeleteChannel this.hihatChannel}}
          @onToggleMute={{fn this.handleToggleMute this.hihatChannel}}
          @onNextSound={{fn this.handleNextSound this.hihatChannel}}
          @onPreviousSound={{fn this.handlePreviousSound this.hihatChannel}}
          @onAddStep={{fn this.handleAddStep this.hihatChannel}}
          @onRemoveStep={{fn this.handleRemoveStep this.hihatChannel}}
          @onStepClick={{this.handleStepClick}}
        />
      </div>
    </section>

    <section class="mt-6">
      <EventLog
        @logger={{this.logger}}
        @emptyMessage="No channel actions logged yet. Interact with any channel track above to log events."
        class="h-48"
      />
    </section>
  </template>
}
